/* eslint-disable no-console */
import Honeybadger from '@honeybadger-io/js'
import { userAuthHeader, removeUserToken } from './apiClient'
import {
  UserAuthenticationError,
  APIUnhandledStatus,
  ServiceError,
} from './errors'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import { newRequestId, readErrorBody } from './http/utils'
import { describeAuthenticationRefusal } from './errors/authenticateUserError'
import type { TEntityId } from '~/types/models'
import type { ITeamMember } from '~/types/teamMember/models'
import type { IUser } from '~/types/user/models'
import { HTTPStatus } from '~/api/httpStatus'
import { Person } from '~/cynqueModules/dataObjects/Person'
import { TeamMember } from '~/cynqueModules/dataObjects/TeamMember'
import { User } from '~/cynqueModules/dataObjects/User'
import { isIServiceError } from '~/cynqueModules/handleAsyncDataErrors'
import { addNotification } from '~/cynqueModules/addNotification'
import { notifySessionExpired } from '~/cynqueModules/notifySessionExpired'
import { useLoggedInUserStore } from '~/stores/loggedInUser'

const mn = '[services/users]'

export class SecretString {
  readonly _value: string
  constructor(value: string) {
    this._value = value
  }

  toString() {
    return '******'
  }

  getValue() {
    return this._value
  }
}

interface DataResponse<D> {
  data: D
}

export interface AuthenticateUserArgs {
  username: string
  password: SecretString
}

interface AuthenticateUserRequestBody {
  username: string
  password: string
}

export interface AuthenticateUserResponse200 {
  authToken: string
  // The api's model has it, but its serializer leaves it out (api #1698), and
  // nothing here reads it.
  getUser?: string
}

export interface AuthenticateUserResponse422 {
  detail: any
}

export interface AuthenticateUserResponse503 {
  detail: any
}

const apiEntityPath = '/api/users'

export async function getUsers({ apiPath = apiEntityPath } = {}) {
  const callingMn = getUsers.name
  const users = await httpGet<IUser[]>({ callingMn, apiPath })
  users.forEach((user) => (user.person = Person(user.person)))
  return users
}

export async function getUsersPreProcessed({ apiPath = apiEntityPath } = {}) {
  const callingMn = getUsersPreProcessed.name
  const rawUsers = await httpGet<IUser[]>({ callingMn, apiPath })
  return rawUsers.map((rawUser) => User(rawUser))
}

export async function getUser(id: TEntityId) {
  const callingMn = getUser.name
  const apiPath = `${apiEntityPath}/${id}`
  const user = await httpGet<IUser>({ callingMn, apiPath })
  if (user?.person) {
    user.person = Person(user.person)
  }
  return user
}

export async function getCurrentUser() {
  return await getUser('current')
}

export async function getTeamMemberForUserId(id: TEntityId) {
  const apiPath = `${apiEntityPath}/${id}/teammember`
  const callingMn = getTeamMemberForUserId.name
  let teamMember = await httpGet<ITeamMember>({ callingMn, apiPath })
  if (teamMember) {
    teamMember = Object.assign(teamMember, TeamMember(teamMember))
  }
  return teamMember
}
export async function authenticateUser({
  username,
  password,
}: AuthenticateUserArgs): Promise<string | undefined> {
  const fn = `${mn} ${authenticateUser.name} |`
  // @ts-ignore
  if (process.server) {
    console.debug(`${mn} Running on server. Not POSTing login data.`)
    return
  }
  const apiPath = `${apiEntityPath}/authentication`
  const url = `${apiPath}`
  const requestBody: AuthenticateUserRequestBody = {
    username,
    password: password.getValue(),
  }
  try {
    console.debug(`${fn} Sending POST request to ${url}.`)
    const response = await fetch(apiPath, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    switch (response.status) {
      case HTTPStatus.OK:
        console.debug(`${mn} Authentication successful`)
        const respBody: DataResponse<AuthenticateUserResponse200> =
          await response.json()

        const responseBody200: AuthenticateUserResponse200 = respBody.data
          ? respBody.data
          : (respBody as unknown as AuthenticateUserResponse200) // This is a kludge until dev and test send the same response again.  JR
        // Note: do not log respBody/responseBody200 — they contain the
        // authToken, which is a live credential and must never reach the
        // console or any console-scraping tooling.
        // The caller hands this to completeLogin, which stores it — the same
        // seam passkey sign-in and claim go through.
        return responseBody200.authToken

      case HTTPStatus.UnprocessableEntity:
        const responseBody422: AuthenticateUserResponse422 =
          await response.json()
        console.log(responseBody422)
        throw new UserAuthenticationError(422, responseBody422.detail)

      case HTTPStatus.Forbidden:
        // readErrorBody, not response.json(): a 403 from something other than
        // the api (an edge proxy's HTML page) must not throw a SyntaxError here.
        const responseBody403 = await readErrorBody(response)
        throw new UserAuthenticationError(
          403,
          describeAuthenticationRefusal(responseBody403)
        )

      case HTTPStatus.NotFound:
      case HTTPStatus.ServiceUnavailable:
        throw new ServiceError(
          response.status,
          'The login service is temporarily unavailable. Please try again later.'
        )

      default:
        const error = new APIUnhandledStatus(
          response.status,
          await response.json()
        )
        Honeybadger.notify(error)
        throw error
    }
  } catch (e) {
    throw e
  }
}

export async function createUser(entity: IUser) {
  const callingMn = createUser.name
  const apiPath = apiEntityPath
  return httpPost<IUser>({ callingMn, apiPath, entity })
}

export async function updateUser(entityData: Partial<IUser>, id: TEntityId) {
  const callingMn = updateUser.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIUser = Partial<IUser>
  return httpPatchMerge<TPartialIUser>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteUser(entityData: IUser) {
  const callingMn = deleteUser.name
  const apiPath = apiEntityPath
  return httpDelete({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function isAuthenticated() {
  const callingMn = isAuthenticated.name
  const apiPath = `${apiEntityPath}/current`
  try {
    const result = await httpGet<IUser>({
      callingMn,
      apiPath,
    })
    return result
  } catch (error) {
    if (isIServiceError(error)) {
      if (error.statusCode === 403 || error.statusCode === 422) {
        notifySessionExpired(error.statusCode)
      } else {
        addNotification({
          type: 'warning',
          message: `Can't authenticate you. Please try again later. If problem persists, call Support.`,
          statusCode: error.statusCode ?? undefined,
        })
      }
    }
    throw error
  }

  // this little hack is here until the api server pipeline is sorted out.
  // return { message: 'User authenticated successfully.' }
}

export async function logoutUser() {
  const fn = `${mn} logoutUser |`
  const apiPath = '/api/users/current/logout'
  const headers = Object.assign(
    { 'x-request-id': newRequestId() },
    userAuthHeader()
  )
  console.debug(`${fn} LOGGING OUT USER`)
  removeUserToken()
  try {
    const response = await fetch(apiPath, {
      method: 'POST',
      headers,
    })
    console.log(
      `Request POST ${apiPath} returned status code ${response.status}.`
    )
  } catch (e) {
    // no-op
  }
  navigateTo('/Login')
  useLoggedInUserStore().clearLoggedInUser()
}
